import { useState } from "react";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const faqs = [{
  question: "Myth: Life insurance is only beneficial after I pass away.",
  answer: "Fact: Modern policies feature 'Living Benefits' through Accelerated Death Benefit riders. This allows you to access a portion of your death benefit today to cover costs associated with critical, chronic, or terminal illnesses—providing financial security when you need it most."
}, {
  question: "Myth: Creative finance is a high-risk strategy for the wealthy.",
  answer: "Fact: Creative finance is simply the application of financial literacy to build wealth through non-traditional formats. It is a three-dimensional outlook that prioritizes efficiency and protection, making it a powerful tool for anyone looking to break free from traditional constraints."
}, {
  question: "Myth: My 401k or IRA is all I need for a secure retirement.",
  answer: "Fact: Traditional retirement accounts lack guaranteed lifetime income. By converting a portion of these assets into Hybrid Annuities, you can create a self-made pension with downside protection and bonuses, ensuring you never outlive your income."
}, {
  question: "Myth: Life insurance is a one-size-fits-all financial product.",
  answer: "Fact: At WealthCraft, we believe there is no single solution. We tailor strategies—from IULs to Hybrid Annuities—to fit your specific goals, health status, and legacy requirements, ensuring your financial plan is as unique as your life."
}, {
  question: "Myth: I'm too young and healthy to worry about coverage now.",
  answer: "Fact: Youth and health are your greatest financial assets. Locking in rates early not only secures lower premiums but also allows more time for cash-value components to grow through compound interest, providing a robust foundation for your future wealth."
}];
export default function CommonQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="31611" id="debunking-the-myths" className="bg-background dark relative overflow-hidden py-24 sm:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={cn("mx-auto max-w-3xl transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          <div className="mb-12 text-center">
            <h2 className="text-foreground font-serif mb-4 text-3xl font-bold tracking-tight sm:text-5xl">
              Debunking the Myths
            </h2>
            <p className="text-muted-foreground font-sans text-lg">
              Separating financial fiction from the reality of creative wealth building.
            </p>
          </div>

          <div className="bg-card/30 border-border/40 shadow-2xl rounded-[2rem] border p-6 backdrop-blur-md sm:p-10">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="border-border/60 group last:border-0 transition-colors duration-500 data-[state=open]:bg-primary/5 rounded-xl px-4" data-index={i}>
                  <AccordionTrigger className={cn("group hover:no-underline py-6", "[&>svg:last-child]:hidden")}>
                    <span className="text-foreground font-serif text-left text-base font-bold sm:text-xl tracking-tight">
                      {faq.question}
                    </span>
                    
                    <span className="text-primary relative flex size-6 shrink-0 items-center justify-center">
                      <Plus className="absolute size-5 transition-all duration-300 ease-in-out group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0" />
                      <Minus className="absolute size-5 rotate-90 opacity-0 transition-all duration-300 ease-in-out group-data-[state=open]:rotate-0 group-data-[state=open]:opacity-100" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-foreground font-serif text-base leading-relaxed pb-6 font-medium border-t border-primary/10 pt-4">
                    <span className="text-primary font-bold block mb-2 uppercase tracking-widest text-xs font-mono">The Fact:</span>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          <div className="mt-10 text-center">
            <p className="text-muted-foreground font-sans">
              Ready to see the full picture?{" "}
              <Link to="/ContactPage" className="text-primary hover:text-primary/80 inline-flex items-center font-bold transition-colors">Consult with our Strategists<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-1 size-4" aria-hidden="true" data-ast-id="f:/src/pages/LifeInsurance/sections/CommonQuestions.tsx|sig:6jciz4" data-ast-start="5467" data-ast-end="5505"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
            </p>
          </div>
        </div>
      </div>
    </section>;
}